import {observable, action} from 'mobx';
import {ownRequest} from '../api/ownFetch';
import Agent from './Agent';
import notificationsStore from './NotificationsStore';
import localizationStore from './LocalizationStore';
import {absorbUntilDone} from '../utils'
import {PREFIX} from '../api/Constants'
import {deleteToken} from '../api/tokenRequest'
import {CategoriesStore} from './Categories'

/**
* @typedef AgentData
* @property {number} agentsUserId,
* @property {string} description,
* @property {string} greetingMessage,
* @property {string} status,
* @property {[]} agentSubscriptionPlans
*/

class Store {
    categoriesStore = new CategoriesStore()

    @observable.shallow
    agents =[]

    @observable
    agentsFetching = false

    @action
    setAgents(agentsList) {
        let agents
        if (this.isAdministrator()) {
            agents = agentsList
        } else {
            agents = [
              ...agentsList.filter(a => a.agentsUser.id === this.currentUserID),
              ...agentsList.filter(a => a.status === 'ACTIVE' && a.agentsUser.id !== this.currentUserID)
            ]
        }
        this.agents = agents.map(a => new Agent(a))
    }

    @action
    addAgent(agent){
        this.agents.push(new Agent(agent))
    }

    @action
    deleteAgent(id){
        this.agents.filter(a => a.id !== id)
    }

    getAgent(id) {
        return this.agents.find(a => {return a.id === id})
    }

    getAgentWithTasks(id) {
        return this.getAgent(id)
    }

    async getAgentsListRequest () {
        this.agentsFetching = true;
        try {
            let response = await ownRequest('GET', '/agentdata', undefined, {includeCharacteristics: true})
            switch (response.status){
                case 200: {
                    const data = await response.json();
                    try {
                        this.setAgents(data.agentsData)

                    } catch (e) {
                        console.error('Wrong response data format')
                    }
                    return Promise.resolve()
                }
                default: {
                    notificationsStore.pushNotification(localizationStore.locale.unknown_error);
                    return Promise.reject()
                }
            }
        } finally {
            this.agentsFetching = false
        }
    }

    async deleteAgentRequest (id) {
        let response = await ownRequest('DELETE', '/agentdata/' + id);
        switch (response.status){
            case 200: {
                this.deleteAgent(id);
                notificationsStore.pushNotification(localizationStore.locale.agent_removed);
                return Promise.resolve()
            }
            default: {
                notificationsStore.pushNotification(localizationStore.locale.unknown_error);
                return Promise.reject()
            }
        }
    }

    /**
     * @param {AgentData} agentData
     * @return {Promise<*>}
     */
    async createAgentRequest(agentData) {
        let response = await ownRequest(
            'POST',
            '/agentdata',
            undefined, // acceptType
            undefined, // params
            {agentData: agentData} // body
        );
        switch (response.status){
            case 200: {
                let data = await response.json();
                try {
                    this.addAgent(data.agentData);
                    notificationsStore.pushNotification(localizationStore.locale.agent_created)
                } catch (e) {
                    console.error('Wrong response data format')
                }
                return Promise.resolve()
            }
            case 403: {
                notificationsStore.pushNotification(localizationStore.locale.agent_creation_reject);
                return Promise.resolve()
            }
            default: {
                notificationsStore.pushNotification(localizationStore.locale.unknown_error);
                return Promise.reject()
            }
        }
    }

    @observable.shallow
    KPIs = []

    @observable
    KPIsFetching = false

    @action
    setKPIs(list) {
        this.KPIs = list
    }

    async getKpiList() {
        this.KPIsFetching = true
        try {
            const response = await ownRequest('GET', '/kpi')
            if (response.status === 200) {
                const data = await response.json()
                this.setKPIs(data.kpiList)
            }
        } finally {
          this.KPIsFetching = false
        }
    }

    getKPI(href) {
        const KPI = this.KPIs.find(kpi => kpi.href.replace(PREFIX, '') === href)
        if (!KPI) {
            return null
        }
        if (!KPI.data) {
            const [path, search] = href.split('?')
            const params = Array.from(new URLSearchParams(search).entries()).reduce((acc, [key, value]) => {
                acc[key] = value
                return acc
            }, {})
            ownRequest('GET', path, undefined, params).then(async response => {
                if (response.status === 200) {
                    KPI.data = (await response.json()).chart.data
                    this.setKPIs(this.KPIs.slice())
                } else {
                    KPI.data = null
                    this.setKPIs(this.KPIs.slice())
                }
            })
        }
        return KPI
    }

    @observable
    currentUser = null

    @observable
    currentUserThumb = null

    setCurrentUser(data) {
        this.currentUser = data
    }

    setCurrentUserThumb(blob) {
        this.currentUserThumb = window.URL.createObjectURL(blob)
    }

    getCurrentUser = absorbUntilDone(async function getCurrentUser() {
        this.getCurrentUserThumb()
        const response = await ownRequest('GET', `/users/${this.currentUserID}`)
        if (response.status === 200) {
            const data = await response.json()
            this.setCurrentUser(data.user)
        }
    }).bind(this)

    getCurrentUserThumb = absorbUntilDone(async function getUserThumb() {
        const response = await ownRequest('GET', `/users/${this.currentUserID}/profileThumbnail`)
        if (response.status === 200) {
            const blob = await response.blob()
            this.setCurrentUserThumb(blob)
        }
    }).bind(this)

    get currentUserID () {
        const id = parseInt(localStorage.getItem('userId'), 10)
        return isNaN(id) ? null : id
    }

    existsAgentWithIdOfCurrentUser() {
        let agentWithIdOfCurrentUser = this.agents.find(a => {return a.userId === this.currentUserID})
        return agentWithIdOfCurrentUser !== undefined;
    }

    get currentUserRole () {
        return localStorage.getItem('role')
    }

    isRegularOrTestUser() {
        return 'USER' === this.currentUserRole || 'TEST_USER' === this.currentUserRole
    }

    isAdministrator() {
      return 'ADMINISTRATOR' === this.currentUserRole
    }

    isOwnTeamMember() {
      return 'OWN_TEAM_MEMBER' === this.currentUserRole
    }

    userCanSeeKPI() {
        return ['ADMINISTRATOR', 'OWN_TEAM_MEMBER', 'SALES_TEAM'].includes(this.currentUserRole)
    }

    logout() {
        return deleteToken().finally(() => {
            localStorage.clear()
            this.agents = []
            this.KPIs = []
            this.currentUser = null
            this.currentUserThumb = null
        })
    }
}

const store = new Store();
export default store;



// WEBPACK FOOTER //
// ./src/store/index.js